import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import moment from "moment";
import { ApiRoutes } from "../../../core/dictionary/api-url";
import axiosService from "../../../core/services/axios-service";
import { ActionDropdown } from "../../../components/designs/action-dropdown";
import { MetricsCard } from "../../../components/designs/matrics-card";
import SideDrawer from "../../../components/designs/side-drawer";
import { Building2, Users, Eye, UserPlus, Trash2, SquareChartGantt, Hand, CalendarClock, BookOpen } from "lucide-react";
import { ListingHeader } from "../../../components/listing-header";
import { SearchAndFiltersListing } from "../../../components/search-filter-listing";
import { RescueRnTable } from "../../../components/designs/rescueRn-table";
import { getInitials, isRescueRnAdmin } from "../../../core/services/global-functions";
import {
  applicationRole,
  individualOrganisationActionType,
} from "../../../core/dictionary/global-enums";
import AddEditCoachTeamMember from "../../../components/add-edit-coach-team-member";
import ViewCoachTeamMember from "../../../components/view-coach-team-member";
import ManageSchedule from "../../../components/manage-schedule";
import ToastService from "../../../core/services/toaster-service";
import Modal from "../../../components/designs/modal";
import AssignCoursesModal from "./assign-courses-modal";
export default function ViewOrganizationListingCoachTeamMembersPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [organizationName, setOrganizationName] = useState("");
  const [totalRows, setTotalRows] = useState(1);
  const [loading, setLoading] = useState(false);
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState(searchTerm);
  const [isSidedrawerOpen, setIsSidedrawerOpen] = useState(false);
  const [actionType, setActionType] = useState("");
  const [rowData, setRowData] = useState(null);
  const [uniqueNumber, setUniqueNumber] = useState(0); // To force re-render
  const [isScheduleDrawerOpen, setIsScheduleDrawerOpen] = useState(false);
  const [showSignupModal, setShowSignupModal] = useState(false);
  const [isAssignCoursesOpen, setIsAssignCoursesOpen] = useState(false);
  const [assignCoursesUser, setAssignCoursesUser] = useState(null);
  const [signupLoading, setSignupLoading] = useState(false);
  const [generatedLink, setGeneratedLink] = useState("");
  const [modalTitle, setModalTitle] = useState("");
  const [modalContent, setModalContent] = useState("");
  const [modalType, setModalType] = useState(""); // "coach" or "team-member"
  const isMounted = useRef(false); // guard: skip debounce on first render
  const columns = [
    {
      name: "Name",
      selector: (row) => row.fullName,
      sortable: true,
      cell: (row) => (
        <div className="d-flex align-items-center">
          <div className="organization-avatar me-3">
            {getInitials(row.fullName)}
          </div>
          {row.fullName}
        </div>
      ),
      width: "250px",
    },
    {
      name: "Title",
      selector: (row) => row.title,
      sortable: true,
      cell: (row) => (
        <span style={{ color: "var(--brand-slate)" }}>{row.title}</span>
      ),
    },
    {
      name: "Contact Number",
      selector: (row) => row.phoneNumber,
      sortable: true,
      cell: (row) => (
        <span style={{ color: "var(--brand-slate)" }}>{row.phoneNumber}</span>
      ),
    },
    {
      name: "Email",
      selector: (row) => row.email,
      sortable: true,
      cell: (row) => (
        <span style={{ color: "var(--brand-slate)" }}>{row.email}</span>
      ),
    },
    {
      name: "Department",
      selector: (row) => row.department,
      sortable: true,
      cell: (row) => (
        <span style={{ color: "var(--brand-slate)" }}>{row.department}</span>
      ),
    },
    {
      name: "Role",
      selector: (row) => row.role,
      sortable: true,
      cell: (row) => (
        <span style={{ color: "var(--brand-slate)" }}>
          {row.role === applicationRole.coach
            ? "Coach"
            : row.role === applicationRole.teamMember
              ? "Team Member"
              : "Admin"}
        </span>
      ),
    },
    {
      name: "Created",
      selector: (row) => row.createdAt,
      sortable: true,
      cell: (row) => (
        <span style={{ color: "var(--brand-slate)" }}>
          {row.createdAt
            ? moment(row.createdAt).format("Do MMM YYYY, h:mm A")
            : ""}
        </span>
      ),
    },
    {
      name: "Actions",
      cell: (row) => (
        <ActionDropdown
          rowData={row}
          actions={getActions(row.role)}
          actionDropClicked={(data) => actionDropClicked(data)}
        />
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "80px",
    },
  ];
  // Below code is for mapping the Actions
  const getActions = (role) => {
    let returnActions = [];
    if (role === applicationRole.coach) {
      returnActions = [
        {
          id: "edit coach",
          label: "Edit coach",
          icon: UserPlus,
          value: individualOrganisationActionType.editCoach,
        },
        {
          id: "view coach",
          label: "View Coach",
          icon: Eye,
          value: individualOrganisationActionType.viewCoach,
        },
        {
          id: "delete",
          label: "Delete Coach",
          icon: Trash2,
          value: individualOrganisationActionType.deleteCoach,
        },
      ];
    } else {
      returnActions = [
        {
          id: "edit team member",
          label: "Edit Team Member",
          icon: UserPlus,
          value: individualOrganisationActionType.editTeam,
        },
        {
          id: "view team member",
          label: "View Team Member",
          icon: Eye,
          value: individualOrganisationActionType.viewTeamMember,
        },
        {
          id: "delete",
          label: "Delete Team Member",
          icon: Trash2,
          value: individualOrganisationActionType.deleteTeamMember,
        },
      ];
    }
    returnActions.push({
      id: "assignCourse",
      label: "Assign Courses",
      icon: BookOpen,
      value: individualOrganisationActionType.assignCourse,
    });
    return returnActions;
  };
  // End of the above code

  const fetchData = async (
    page = 1,
    size = 10,
    sort = "createdAt",
    order = "desc",
    search = "",
  ) => {
    setLoading(true);
    const res = await axiosService.get(
      ApiRoutes.COACH_TEAM_MEMBER_LISTING + "/" + id,
      {
        params: {
          perPage: size,
          currentPage: page,
          sortBy: sort,
          sortOrder: order,
          searchStr: search || "",
        },
      },
    );
    setData(res.data.data);
    setOrganizationName(
      res.data.organization_name || res?.data?.data[0]?.organization_name || "",
    );
    setTotalRows(res.data.total);
    setLoading(false);
  };

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      return; // skip on first render — fetchData already runs via the effect below
    }
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 400);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  useEffect(() => {
    fetchData(currentPage, perPage, sortBy, sortOrder, debouncedSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, perPage, sortBy, sortOrder, debouncedSearch]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };
  const handlePerRowsChange = (newPerPage) => {
    setPerPage(newPerPage);
    setCurrentPage(1);
  };
  const handleSort = (column, sortDirection) => {
    setSortBy(column.sortField || "createdAt");
    setSortOrder(sortDirection || "desc");
  };
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };
  const addCoach = () => {
    // Logic to add coach
    setRowData(null);
    setUniqueNumber(Math.random()); // Change unique number to force re-render
    setActionType(individualOrganisationActionType.addCoach);
    setTimeout(() => {
      setIsSidedrawerOpen(true); // Open the sidedrawer
    }, 100);
  };
  const addTeamMember = () => {
    // Logic to add team member
    setRowData(null);
    setUniqueNumber(Math.random());
    setActionType(individualOrganisationActionType.addTeamMember);
    setTimeout(() => {
      setIsSidedrawerOpen(true); // Open the sidedrawer
    }, 100);
  };

  const commonActionOrganization = (data) => {
    setRowData(data);
    setIsSidedrawerOpen(true);
  };
  const actionDropClicked = (data) => {
    if (
      data.action === individualOrganisationActionType.editCoach ||
      data.action === individualOrganisationActionType.editTeam ||
      data.action === individualOrganisationActionType.addCoach ||
      data.action === individualOrganisationActionType.addTeamMember ||
      data.action === individualOrganisationActionType.viewCoach ||
      data.action === individualOrganisationActionType.viewTeamMember
    ) {
      // Edit and View is Managed by this Condition only
      setActionType(data.action);
      commonActionOrganization(data.rowData);
    } else if (data.action === individualOrganisationActionType.assignCourse) {
      setAssignCoursesUser(data.rowData);
      setIsAssignCoursesOpen(true);
    } else if (data.action === "delete") {
      deleteOrganization(data.rowData);
    }
  };

  const deleteOrganization = (orgId) => {
    // Logic to delete organization
    console.log("Delete organization with ID:", orgId);
  };
  const getNewOrganizationsCount = (organizations) => {
    // Get the first and last day of the previous calendar month
    const startOfLastMonth = moment().subtract(1, "months").startOf("month");
    const endOfLastMonth = moment().subtract(1, "months").endOf("month");
    return organizations.filter((org) =>
      moment(org.createdAt).isBetween(
        startOfLastMonth,
        endOfLastMonth,
        null,
        "[]",
      ),
    ).length;
  };
  const generateSignupLink =  (type) => {
    setGeneratedLink("");
    setShowSignupModal(true);
    setModalType(type);
    if(type === "coach"){
      setModalTitle("Generate Coach Sign-up Link");
      setModalContent("Click Generate to create a self sign-up link for coaches for this organization.");
    } else {
      setModalTitle("Generate Team Member Sign-up Link");
      setModalContent("Click Generate to create a self sign-up link for team members for this organization.");
    }
    // Logic to generate self sign-up link for coaches
    console.log("Generate self sign-up link for coaches");
    
  };
  const handleGenerateSignupLinkConfirmed = async (type) => {
    try {
      setSignupLoading(true);
      // call API to create signup link (replace with actual endpoint)
      // example:
      // const res = await axiosService.post(ApiRoutes.GENERATE_COACH_SIGNUP_LINK, { organizationId: id });
      // setGeneratedLink(res.data.link);

      // placeholder simulation:
      await new Promise((r) => setTimeout(r, 600));
      const fakeLink = `${window.location.origin}/signup/${type}/${id}`;
      setGeneratedLink(fakeLink);
    } catch (err) {
      console.error(err);
      ToastService.error("Failed to generate signup link");
    } finally {
      setSignupLoading(false);
    }
  };
  const openTrainingScheduleModal = () => {
    setIsScheduleDrawerOpen(true);
  };
  return (
    <>
      <ListingHeader
        title={"Organization"}
        breadCrumb={[
          { label: "Dashboard", href: "/admin/dashboard" },
          { label: "Organizations", href: "/admin/organizations" },
          {
            label: organizationName ? organizationName : "",
            href: "/admin/organizations",
          },
        ]}
        actions={[
          ...(isRescueRnAdmin()
            ? [
                {
                  label: "Manage Attendance",
                  className: "btn btn-rescuern-primary",
                  icon: SquareChartGantt,
                  onClick: () => navigate(`/admin/organizations/${id}/manage-attendance`),
                },
              ]
            : []),
          ...(isRescueRnAdmin()
            ? [
                {
                  label: "Manage Schedule",
                  className: "btn btn-rescuern-primary",
                  icon: CalendarClock,
                  onClick: openTrainingScheduleModal,
                },
              ]
            : []),
        ]}
        dropdown={{
          label: "Actions",
          icon: Hand,
          items: [
            {
              label: "Add Coach",
              // provide a handler in your component and reference it here
              onClick: () => addCoach(),
            },
            {
              label: "Add Team Member",
              // provide a handler in your component and reference it here
              onClick: () => addTeamMember(),
            },
            {
              label: "Generate Self Sign-up link for Coach",
              // provide a handler in your component and reference it here
              onClick: () => generateSignupLink("coach"),
            },
            {
              label: "Generate Self Sign-up link for Team Members",
              onClick: () => generateSignupLink("team-member"),
            },
            {
              label: "Set Online Training Start and End Time",
              onClick: () => openTrainingScheduleModal(),
            },
          ],
        }}
      />

      <div className="flex-grow-1 p-4">
        <div className="container-fluid p-0">
          <div className="row g-4 mb-4">
            <div className="col-xl-6 col-lg-6">
              <MetricsCard
                title="Total Users"
                value={loading ? "--" : totalRows}
                icon={Building2}
              />
            </div>
            <div className="col-xl-6 col-lg-6">
              <MetricsCard
                title="New This Month"
                value={
                  loading ? "--" : totalRows - getNewOrganizationsCount(data)
                }
                icon={Users}
              />
            </div>
          </div>
          <div className="mb-4">
            <SearchAndFiltersListing
              placeholder="Search coaches and team members..."
              searchTerm={searchTerm}
              onSearchChange={handleSearchChange}
            />
          </div>
          <RescueRnTable
            data={data}
            loading={loading}
            columns={columns}
            totalRows={totalRows}
            loadingTableText="Loading Coaches and Team Members..."
            handleSort={handleSort}
            onChangePage={(page) => handlePageChange(page)}
            handlePerRowsChange={(newPerPage) =>
              handlePerRowsChange(newPerPage)
            }
            perPage={perPage}
          ></RescueRnTable>
        </div>
      </div>
      {/* End of the above code */}
      {/* Below is the Modal Popup Component for Generate Self Sign-up link and Training Schedule which is common for both Coach and Team Members */}
      <Modal
        isOpen={showSignupModal}
        title={modalTitle}
        onClose={() => {
          setShowSignupModal(false);
          setGeneratedLink("");
        }}
        onConfirm={() => handleGenerateSignupLinkConfirmed(modalType)}
        confirmLabel={generatedLink ? "Regenerate" : "Generate"}
        confirmDisabled={signupLoading}
      >
        <div>
          <p style={{ color: "var(--brand-slate)" }}>
            {modalContent}
          </p>

          {generatedLink ? (
            <div style={{ marginTop: 12 }}>
              <label className="form-label">Generated Link</label>
              <div style={{ display: "flex", gap: 8 }}>
                <input
                  readOnly
                  value={generatedLink}
                  className="form-control"
                  style={{ flex: 1 }}
                />
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => {
                    navigator.clipboard?.writeText(generatedLink);
                    ToastService.success("Link copied to clipboard");
                  }}
                >
                  Copy
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </Modal>
      {/* End of the above code */}
      {/* Below is the Assign Courses Modal */}
      <AssignCoursesModal
        isOpen={isAssignCoursesOpen}
        user={assignCoursesUser}
        onClose={() => setIsAssignCoursesOpen(false)}
        onSuccess={() => setIsAssignCoursesOpen(false)}
      />
      {/* End of the above code */}
      {/* Below is the sidedrawer component */}
      <SideDrawer
        title={
          actionType === individualOrganisationActionType.addCoach
            ? "Add Coach"
            : actionType === individualOrganisationActionType.editCoach
              ? "Edit Coach"
              : actionType === individualOrganisationActionType.viewCoach
                ? "View Coach"
                : actionType === individualOrganisationActionType.addTeamMember
                  ? "Add Team Member"
                  : actionType === individualOrganisationActionType.editTeam
                    ? "Edit Team Member"
                    : actionType ===
                        individualOrganisationActionType.viewTeamMember
                      ? "View Team Member"
                      : ""
        }
        isOpen={isSidedrawerOpen}
        onClose={() => setIsSidedrawerOpen(false)}
        closeOnOverlay={false}
      >
        {/* Below code is for the Adding the Coach and Team Members */}
        {actionType === individualOrganisationActionType.addCoach ||
        actionType === individualOrganisationActionType.addTeamMember ||
        actionType === individualOrganisationActionType.editTeam ||
        actionType === individualOrganisationActionType.editCoach ? (
          <AddEditCoachTeamMember
            uniqueNumber={uniqueNumber}
            type={actionType}
            id={rowData?._id ? rowData._id : null}
            organizationId={id ? id : null}
            onClose={() => {
              setIsSidedrawerOpen(false);
              setActionType(null);
            }}
            onSuccess={(res) => {
              setIsSidedrawerOpen(false);
              fetchData(currentPage, perPage, sortBy, "desc", debouncedSearch);
            }}
          ></AddEditCoachTeamMember>
        ) : null}
        {/* End of the above code */}
        {/* Below code is for viewing the Coach and Team Members */}
        {actionType === individualOrganisationActionType.viewCoach ||
        actionType === individualOrganisationActionType.viewTeamMember ? (
          <ViewCoachTeamMember
            type={actionType}
            id={rowData?._id ? rowData._id : null}
          ></ViewCoachTeamMember>
        ) : null}
        {/* End of the above code */}
      </SideDrawer>
      {/* End of the above code */}
      {/* Below is the Manage Schedule sidedrawer — visible to RescueRn Admin only */}
      <SideDrawer
        title="Manage Schedule"
        isOpen={isScheduleDrawerOpen}
        onClose={() => setIsScheduleDrawerOpen(false)}
        closeOnOverlay={false}
      >
        <ManageSchedule
          organizationId={id}
          isOpen={isScheduleDrawerOpen}
          onSuccess={() => setIsScheduleDrawerOpen(false)}
        />
      </SideDrawer>
      {/* End of the above code */}
    </>
  );
}
